import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const hasPuesto = useMemo(() => Boolean(user?.id_generado), [user]);

  // Redirigir supervisores al dashboard de supervisión
  useEffect(() => {
    if (user && user.rol === 'S') {
      navigate('/dashboard-supervision', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchOverview = async () => {
      if (!hasPuesto) {
        setLoading(false);
        setError('Tu usuario no tiene un puesto asignado. Solicita a un administrador que configure tu acceso.');
        return;
      }

      setLoading(true);
      setError('');

      try {
        const url = new URL(getApiUrl('DASHBOARD', 'OVERVIEW'));
        url.searchParams.set('idGenerado', user.id_generado);
        url.searchParams.set('username', user.username || '');
        url.searchParams.set('appid', user.appid || '');

        const response = await fetch(url.toString());
        const data = await response.json();

        if (response.ok && data.success) {
          setOverview(data.data);
        } else {
          setError(data.error || 'No fue posible cargar la información del dashboard.');
        }
      } catch (err) {
        console.error('Error cargando overview del dashboard:', err);
        setError('Error de conexión al cargar la información del dashboard.');
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, [hasPuesto, user]);

  const lineChartData = useMemo(() => {
    if (!overview || overview.eventosPorDia.length === 0) {
      return null;
    }

    const labels = overview.eventosPorDia.map((item) => {
      const date = new Date(item.fecha);
      return date.toLocaleDateString('es-EC', { day: '2-digit', month: 'short' });
    });

    return {
      labels,
      datasets: [
        {
          label: 'Total eventos',
          data: overview.eventosPorDia.map((item) => item.total || 0),
          borderColor: '#6366F1',
          backgroundColor: 'rgba(99, 102, 241, 0.2)',
          tension: 0.4,
          fill: true
        },
        {
          label: 'Visitantes',
          data: overview.eventosPorDia.map((item) => item.visitantes || 0),
          borderColor: '#22C55E',
          backgroundColor: 'rgba(34, 197, 94, 0.2)',
          tension: 0.4,
          fill: true
        },
        {
          label: 'Vehículos',
          data: overview.eventosPorDia.map((item) => item.vehiculos || 0),
          borderColor: '#F97316',
          backgroundColor: 'rgba(249, 115, 22, 0.2)',
          tension: 0.4,
          fill: true
        }
      ]
    };
  }, [overview]);

  return (
    <Layout>
      <div className="p-3 sm:p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        </div>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow">
            <div className="inline-flex items-center space-x-2">
              <svg className="animate-spin h-5 w-5 text-indigo-600" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="text-sm text-gray-600">Cargando resumen del día…</span>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
            <p className="text-sm font-medium">{error}</p>
          </div>
        ) : overview ? (
          <>
            <section>
              <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Resumen de la jornada</p>
                    <p className="text-sm text-gray-500 mt-1">Actividad registrada hasta el momento</p>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Visitantes Hoy</span>
                    <span className="text-2xl sm:text-3xl font-bold text-green-600 mt-2">{overview.resumen.visitantesHoy}</span>
                    <span className="text-[11px] sm:text-xs text-gray-500 mt-1">Ingresos de visitantes confirmados.</span>
                  </div>
                  <div className="flex flex-col border-l border-dashed border-gray-200 pl-4">
                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Vehículos Hoy</span>
                    <span className="text-2xl sm:text-3xl font-bold text-orange-500 mt-2">{overview.resumen.vehiculosHoy}</span>
                    <span className="text-[11px] sm:text-xs text-gray-500 mt-1">Control de vehículos registrados.</span>
                  </div>
                </div>
              </div>
            </section>

            <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Tendencia de eventos</h2>
                    <p className="text-xs text-gray-500">Comparativa de los últimos 7 días</p>
                  </div>
                </div>
                {lineChartData ? (
                  <div className="h-64">
                    <Line
                      data={lineChartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'bottom'
                          }
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            ticks: {
                              precision: 0
                            }
                          }
                        }
                      }}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Aún no hay eventos registrados en los últimos días.</p>
                )}
              </div>
            </section>

            <section className="bg-white border border-gray-200 rounded-xl p-6 shadow">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Actividad reciente</h2>
                  <p className="text-xs text-gray-500">Últimos movimientos registrados en la bitácora</p>
                </div>
              </div>
              {overview.recientes.length === 0 ? (
                <p className="text-sm text-gray-500">Aún no hay actividad registrada.</p>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {overview.recientes.map((item, index) => {
                    const fecha = new Date(item.hora);
                    return (
                      <li key={`${item.tipo_evento}-${item.hora}-${index}`} className="py-3 flex items-start justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{item.tipo_evento}</p>
                          <p className="text-xs text-gray-500 mt-1">{item.observacion || 'Sin observaciones'}</p>
                        </div>
                        <span className="text-xs text-gray-400">
                          {fecha.toLocaleDateString('es-EC', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </>
        ) : (
          <p className="text-sm text-gray-500">Aún no hay información disponible para mostrar.</p>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
